import {uiMsg} from '../utils/Languages';
import {observable, autorun, computed} from 'mobx';
import * as LoadLevel from './LoadLevel';

function isNumeric(n) {
  if(n==null) return false;
  return !isNaN(+n) && isFinite(n);
}

// Utility function
function indentString(ind_count) {
  var blocks=[];
  for(var i=0;i<ind_count;i++) {
    blocks.push("\xa0\xa0\xa0\xa0");
  }
  return blocks.join("");
}

// For now this is just a blind copy of the rows from the database.
function createLocationFromDBRow(row) {
  var l = new Location();
  l.id=row.id;
  l.id_parent=row.id_parent;
  l.name=row.name;
  l.id_type=row.id_type;
  l.type=row.type;
  l.volume_m3=row.volume_m3;
  l.height = row.height;
  l.length = row.length;
  l.width = row.width;
  l.last_disinfected = row.last_disinfected;
  l.path = row.path;
  l.deleted = row.deleted;
  l.default_job_type = row.default_job_type;
  l.user_count = +row.user_count || 0;
  l.beacons = row.beacons;
  return l;
}


class Location {
  @observable id;
  @observable id_parent;
  @observable name;
  @observable id_type;
  @observable type;
  @observable path;
  @observable last_disinfected;
  @observable load_level;
  @observable loading;
  @observable height=2.4;
  @observable length;
  @observable width;
  @observable volume_m3;
  @observable children=[];
  @observable deleted;
  @observable default_job_type;
  @observable user_count = 0;
  @observable beacons=[];
  @observable new_beacon_id;
  // This is the state we use to decide whether the UI shows the children
  // of this location or not (open/close) in a tree-view.
  ui_open_state = false;

  constructor() {
    this.reset();
  }
  // In use when registering a new location :
  reset() {
    this.id=null;
    this.id_parent=null;
    this.name=null;
    // The default is a standard Room, not an area that can contain other rooms
    this.id_type='R';
    this.type=null;
    this.path=null;
    this.height = 2.4;
    this.length = null;
    this.width = null;
    this.volume_m3 = null;
    this.last_disinfected = null;
    this.load_level=LoadLevel.NONE;
    this.loading=false;
    this.children=[];
    this.deleted=false;
    this.default_job_type=null;
    this.user_count=0;
    this.beacons = [];
    this.new_beacon_id=null;
  }

  // Before we use this object to update the database, we make sure that
  // field values with only empty spaces etc. are set to null
  trimNumericFields() {
    let numeric_fields = ['height','length','width','volume_m3'];
    numeric_fields.forEach(f => {
      // If the field is a string that when trimmed is empty:
      if(this[f] && this[f].trim && !this[f].trim()) {
        // Set them to null;
        this[f]=null;
      }
      // run them through the simple conversion trick where we accept both
      // , and . as decimal separators:
      if(this[f]) {
        console.log("converting fields to numeric values")
        this[f]=this.convertToNum(this[f]);
      }
    })
  }

  checkIfValid() {
    if(!this.dimensionsParsable) return false;
    return  this.isNameValid && this.id_type!=null && this.validVolume;
  }

  @computed get numberOfUsers() {
    var userCount = this.user_count;
    if(this.children) this.children.forEach( r => {
      userCount+=r.numberOfUsers;
    });
    return userCount;
  }

  @computed get numberOfChildRooms() {
    var roomCount = 0;
    if(this.children) this.children.forEach( r => {
      if(r.id_type=="R") {
        roomCount+=1;
      }
      else {
        roomCount+=r.numberOfChildRooms;
      }
    })
    return roomCount;
  }

  // The further down the location tree, the longer the path:
  @computed get indent() {
    if(this.path==null) return 0;
    return this.path.length - 1;
  }
  // A string that can be put in front of the location to give
  // a text-based tree-structure
  @computed get indentStr() {
    var i = this.indent;
    if(i==0) return "";
    return indentString(i);
  }

  convertToNum(number) {
    if(typeof(number)=='string') {
      number=number.replace(",",".");
    }
    return +number;
  }

  @computed get computedVolume() {
    if(!this.height) return null;
    if(!this.length) return null;
    if(!this.width) return null;
    return this.convertToNum(this.height)
    * this.convertToNum(this.length)
    * this.convertToNum(this.width);
  }

  @computed get computedVolumeDifferentFromSet() {
    var comp = this.computedVolume;
    if(!comp) return false;
    if(!this.volume_m3) return false;
    // Allow some rounding error, above that, report that there is a difference
    // between the computed value and the manually set volume_m3...
    return Math.abs(+this.volume_m3 - comp) > 0.01;
  }

  // Picking the most important volume-related error message:
  @computed get volumeErrorMessage() {
    if(this.computedVolumeDifferentFromSet) {
      return uiMsg('LOC_COMPUTED_VOLUME_DIFFER',"Computed volume differs from manually set volume");
    }
    if(this.finalVolume > 250.0001) {
      return uiMsg('LOC_MAX_ALLOWED_VOLUME',"Max allowed volume is 250 m³");
    }
    if(!this.numericVolume) {
      return uiMsg('LOC_VOLUME_MUST_BE_VALID_M3',"Volume must be a valid number of m³");
    }
    if(this.lesserThanMinimumVolume) {
      return uiMsg('LOC_VOLUME_MUST_BE_GREATER_THAN_MIN',"Volume must be at least 4 m³");
    }
    return null;
  }

  @computed get finalVolume() {
    if(isNumeric(this.computedVolume)) return this.computedVolume;
    return this.volume_m3;
  }

  @computed get numericVolume() {
    return isNumeric(this.finalVolume);
  }

  @computed get lesserThanMinimumVolume() {
    return this.finalVolume<4.00;
  }

  // Verify that we do have a room value, and that it within valid range
  @computed get validVolume() {
    if(isNumeric(this.computedVolume)  && this.computedVolume>=4.00 && this.computedVolume<=250.01) return true;
    return (isNumeric(this.volume_m3 ) && this.volume_m3>=4.00 && this.volume_m3<=250.01)
  }

  @computed get readable() {
    return  this.id+" "+this.name+" "+this.typeName+" ";
  }
  // All location types but room may contain other locations :
  @computed get isContainer() {
    return  this.id_type!='R';
  }

  // verify if height, length and width are valid numbers or null
  @computed get dimensionsParsable() {
    if(this.length && !isNumeric(this.convertToNum(this.length))) return false;
    if(this.width && !isNumeric(this.convertToNum(this.width))) return false;
    if(this.height && !isNumeric(this.convertToNum(this.height))) return false;
    return true;
  }

  // Validation helper methods for registering new location :
  @computed get isValid() {
    // Don't allow a location to be it's own parent:
    if(this.id==this.id_parent) return false;
    if(!this.dimensionsParsable) return false;
    return  this.isNameValid && this.id_type!=null &&
    // If the location is marked for deletion, or a container area
    // we don't care about volume
    (this.deleted || this.isContainer || this.validVolume);
  }
  @computed get isNameValid() {
    return this.name && this.name.trim() > "";
  }
  // Get typename from type-id :
  @computed get typeName() {
    if(!this.id_type) return "room";
    return  {'B'  : uiMsg('LOC_BUILDING','Building'),
             'S'  : uiMsg('LOC_SECTION','Section'),
             'E'  : uiMsg('LOC_ENTRANCE','Entrance'),
             'A'  : uiMsg('LOC_AREA','Area')    ,
             'R'  : uiMsg('LOC_ROOM','Room')    ,
             'F'  : uiMsg('LOC_FLOOR','Floor')}[this.id_type];
  }
  // Make a copy (to insert into machineList after sent to server)
  clone() {
    var res = new Location();
    Object.assign(res,this);
    // Shallow clone of child array :
    res.children=[];
    this.children.forEach(function(child) { res.children.push(child); });
    return res;
  }
}

// Make utility method available from Class (function)
Location.createFromDBRow=createLocationFromDBRow;

export default Location;



/** WEBPACK FOOTER **
 ** ./src/DataModel/Location.js
 **/